import { useClubSlug } from 'components/Clubs/hooks';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import useSWR from 'swr';
import useAuth from './useAuth';

export interface useSubscriptionStatusOptions {
    clubSlug?: string;
    /** Plan key to check, e.g. 'trainingplantool'. Empty string = no gate (always passes). */
    permission?: string;
    redirect?: string;
    delay?: number;
}

export interface SubscriptionStatusData {
    /** List of plan keys the club can access, e.g. ['trainingplantool', 'invoicetool'] */
    subscribed_plans: string[];
    /** Per-plan override_until ISO string; null means no override was ever triggered for that plan. */
    plan_overrides: Record<string, string | null>;
    /** The user owns the club with full access, only their subscription unlocks it. */
    can_book?: boolean;
}

/** Checks subscription status for a club via SWR (cached per clubSlug — no repeated network requests). */
export const useSubscriptionStatus = ({
    redirect,
    clubSlug: _clubSlug,
    permission,
    delay = 3000
}: useSubscriptionStatusOptions) => {
    const clubSlug = useClubSlug(_clubSlug);
    const { isAuthenticated } = useAuth();
    const { replace } = useRouter();

    const { data, isLoading, error } = useSWR<SubscriptionStatusData>(
        isAuthenticated && clubSlug ? `subscriptions/status/${clubSlug}` : null
    );

    // No gate when permission is empty; otherwise check plan is in subscribed_plans.
    // Errors are NOT included here — a transient revalidation error (e.g. on focus) would
    // flip permissionError while data is still cached, unmounting children and looking like a refresh.
    const permissionError = useMemo(
        () => !!permission && data !== undefined && !(data.subscribed_plans ?? []).includes(permission),
        [data, permission]
    );

    useEffect(() => {
        if (permissionError && redirect) {
            const timeout = setTimeout(() => replace(redirect), delay);
            return () => clearTimeout(timeout);
        }
    }, [redirect, delay, permissionError]);

    return {
        data,
        isAuthenticated,
        permissionError,
        hasAccess: !permissionError,
        isLoading,
        error
    };
};

export default useSubscriptionStatus;
